import type { SceneObject } from "../../SceneHost";
import { placeHtmlObject } from "../htmlLayerObject";
import {
  type CodeAction,
  type CodeLanguage,
  CODE_RUN_TIMEOUT_MS,
  languageLabel,
  readCodeState,
} from "../../../lib/board/codeLogic";
import { buildWebSrcDoc, runCodeWidget, type CodeRunPhase } from "../../../lib/board/codeRunners";
import { jsImportHint, pythonImportHint } from "../../../lib/board/codePackages";
import {
  mountCodeEditor,
  readCodeValuesFromCard,
  getCodeEditor,
  type CodeEditorLanguage,
} from "../codeEditorView";
import {
  applyRemoteToEditor,
  createLiveTextSender,
  type LiveTextSender,
} from "../liveTextSync";


export type CodeRenderOpts = {
  canEdit?: boolean;
  onCodeAction?: (objectId: string, action: CodeAction) => void;
  onRequestSelect?: (objectId: string) => void;
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.setAttribute("class", className);
  return node;
}

function stopProp(node: Element) {
  for (const type of ["pointerdown", "mousedown", "click", "dblclick"] as const) {
    node.addEventListener(type, (ev) => ev.stopPropagation());
  }
}

const LANGS: CodeLanguage[] = ["python", "javascript", "web"];
/** Как часто вывод консоли уходит другим, пока код выполняется. */
const STDOUT_STREAM_MS = 400;
const STDOUT_PLACEHOLDER = "Вывод появится здесь…";

function runPhaseLabel(phase: CodeRunPhase, detail?: string): string {
  switch (phase) {
    case "preparing":
      return "Подготовка…";
    case "loading_runtime":
      return detail || "Загрузка Python…";
    case "loading_packages":
      return detail ? `Загрузка: ${detail}` : "Загрузка библиотек…";
    case "running":
      return detail || "Выполнение кода…";
    case "done":
      return "Готово";
    case "error":
      return "Ошибка";
    default:
      return detail || "…";
  }
}

/** id виджетов, в чьём textarea сейчас печатают (foreignObject плохо держит activeElement). */
const codeTypingIds = new Set<string>();

export function isCodeWidgetTyping(objectId?: string): boolean {
  if (objectId) return codeTypingIds.has(objectId);
  return codeTypingIds.size > 0;
}

export function clearCodeWidgetTyping(objectId?: string): void {
  if (objectId) codeTypingIds.delete(objectId);
  else codeTypingIds.clear();
}

/** Обновить transform/size без пересборки DOM (для focused typing). */
export function syncCodeObjectLayout(node: HTMLElement, obj: SceneObject): void {
  const w = Math.max(280, Number(obj.w) || 520);
  const h = Math.max(240, Number(obj.h) || 420);
  placeHtmlObject(node, obj, w, h);
}

/** Совпадает ли сохранённый foreignObject с language в store (иначе вкладки «залипают»). */
export function codeDomMatchesObject(node: HTMLElement, obj: SceneObject): boolean {
  const card = node.querySelector(".wb-code-card") as HTMLElement | null;
  if (!card) return false;
  const domLang = card.dataset.language || "";
  const state = readCodeState(obj as Parameters<typeof readCodeState>[0]);
  return domLang === state.language;
}

/**
 * Чужие изменения — в уже открытую карточку, без пересборки: текст в редакторах
 * (курсор не прыгает), вывод консоли, превью HTML после «Обновить превью».
 * false — структура разошлась (сменили язык), карточку надо пересобрать.
 */
export function syncCodeObjectOnGroup(node: HTMLElement, obj: SceneObject): boolean {
  if (!codeDomMatchesObject(node, obj)) return false;
  const card = node.querySelector(".wb-code-card") as HTMLElement | null;
  if (!card) return false;
  syncCodeObjectLayout(node, obj);
  const state = readCodeState(obj as Parameters<typeof readCodeState>[0]);

  const fieldValue: Record<string, string> = {
    source: state.source,
    js: state.js,
    html: state.html,
    css: state.css,
  };
  for (const host of card.querySelectorAll<HTMLElement>("[data-code-editor]")) {
    const field = host.dataset.codeField ?? "";
    if (field in fieldValue) applyRemoteToEditor(host, fieldValue[field]!);
  }

  // Пока код выполняется у нас, консоль ведёт наш запуск.
  const runningHere = card.classList.contains("is-running");
  const out = card.querySelector(".wb-code-card__stdout") as HTMLElement | null;
  if (out && !runningHere) {
    const text = state.stdout || STDOUT_PLACEHOLDER;
    if (out.textContent !== text) out.textContent = text;
  }

  const seq = String(state.runSeq);
  if (card.dataset.runSeq !== seq) {
    card.dataset.runSeq = seq;
    const frame = card.querySelector(".wb-code-card__iframe") as HTMLIFrameElement | null;
    if (frame && !runningHere) {
      const doc = buildWebSrcDoc(state.html, state.css, state.js);
      if (frame.srcdoc !== doc) frame.srcdoc = doc;
    }
  }
  return true;
}

/** Стили внутри foreignObject — иначе в части браузеров внешний CSS не цепляется. */
const FO_CSS = `
.wb-code-card{box-sizing:border-box;width:100%;height:100%;display:flex;flex-direction:column;gap:6px;padding:8px;border-radius:12px;background:linear-gradient(165deg,#0f172a 0%,#1e293b 100%);border:1px solid #334155;box-shadow:0 8px 24px rgba(15,23,42,.28);color:#e2e8f0;font-family:"Segoe UI",system-ui,sans-serif;overflow:hidden;cursor:default}
.wb-code-card,.wb-code-card *{cursor:default}
.wb-code-card__head{display:flex;align-items:center;justify-content:space-between;gap:8px;flex-shrink:0}
.wb-code-card__title{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#94a3b8}
.wb-code-card__edit{appearance:none;border:1px solid #475569;background:#1e293b;color:#e2e8f0;border-radius:7px;font-size:11px;font-weight:600;padding:3px 8px;cursor:pointer}
.wb-code-card__edit:hover{background:#334155}
.wb-code-card__tabs{display:flex;gap:4px;flex-shrink:0;flex-wrap:wrap}
.wb-code-card__tab{appearance:none;border:1px solid transparent;background:transparent;color:#94a3b8;border-radius:7px;font-size:11px;font-weight:600;padding:4px 8px;cursor:pointer}
.wb-code-card__tab.is-active{background:#134e4a;color:#ccfbf1;border-color:#0f766e}
.wb-code-card__tab:disabled{opacity:.55;cursor:default}
.wb-code-card__body{flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:6px}
.wb-code-card__body--web{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.wb-code-card__editors{display:flex;flex-direction:column;gap:6px;min-height:0;overflow:auto}
.wb-code-card__editor{display:flex;flex-direction:column;gap:3px;min-height:0;flex:1}
.wb-code-card__label{font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em}
.wb-code-card__editor-host{flex:1 1 auto;min-height:64px;width:100%;overflow:hidden;border-radius:8px;cursor:text}
.wb-code-card__editor-host,.wb-code-card__editor-host *{cursor:text}
.wb-code-card__editor-host .cm-editor{height:100%;min-height:64px}
.wb-code-card__textarea{flex:1 1 auto;min-height:64px;width:100%;box-sizing:border-box;resize:none;border-radius:8px;border:1px solid #334155;background:#020617;color:#e2e8f0;font-family:ui-monospace,"Cascadia Code",Consolas,monospace;font-size:12px;line-height:1.4;padding:8px;outline:none;cursor:text}
.wb-code-card__textarea:focus{border-color:#14b8a6}
.wb-code-card__preview{display:flex;flex-direction:column;gap:3px;min-height:0}
.wb-code-card__iframe{flex:1 1 auto;min-height:120px;width:100%;border:1px solid #334155;border-radius:8px;background:#fff}
.wb-code-card__foot{display:flex;align-items:center;gap:8px;flex-shrink:0;flex-wrap:wrap}
.wb-code-card__run{appearance:none;border:none;border-radius:8px;background:#0d9488;color:#ecfdf5;font-size:12px;font-weight:700;padding:6px 12px;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.wb-code-card__run:hover{background:#0f766e}
.wb-code-card__run:disabled{opacity:.65;cursor:wait}
.wb-code-card__run.is-busy::before{content:"";width:12px;height:12px;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:wb-code-spin .75s linear infinite;flex-shrink:0}
.wb-code-card__status{display:none;align-items:center;gap:6px;font-size:11px;font-weight:600;color:#5eead4;flex:1 1 auto;min-width:0}
.wb-code-card__status.is-visible{display:inline-flex}
.wb-code-card__status-dot{width:8px;height:8px;border-radius:50%;background:#14b8a6;flex-shrink:0;animation:wb-code-pulse 1.1s ease-in-out infinite}
.wb-code-card.is-running{box-shadow:0 8px 28px rgba(20,184,166,.22),0 0 0 1px rgba(45,212,191,.35)}
.wb-code-card.is-running .wb-code-card__stdout{border-color:#14b8a6;box-shadow:inset 0 0 0 1px rgba(20,184,166,.25)}
.wb-code-card__stdout.is-live{color:#ccfbf1}
@keyframes wb-code-spin{to{transform:rotate(360deg)}}
@keyframes wb-code-pulse{0%,100%{opacity:.45;transform:scale(.85)}50%{opacity:1;transform:scale(1)}}
.wb-code-card__download{appearance:none;border:1px solid #475569;border-radius:8px;background:#1e293b;color:#e2e8f0;font-size:12px;font-weight:650;padding:6px 12px;cursor:pointer}
.wb-code-card__download:hover{background:#334155;border-color:#64748b}
.wb-code-card__hint{font-size:10px;color:#64748b}
.wb-code-card__stdout{flex:0 0 auto;box-sizing:border-box;width:100%;min-height:48px;height:88px;max-height:220px;margin:0;overflow:auto;resize:vertical;border-radius:8px;border:1px solid #334155;background:#020617;color:#a7f3d0;font-family:ui-monospace,Consolas,monospace;font-size:11px;line-height:1.35;padding:6px 8px;white-space:pre;tab-size:8;cursor:text}
.wb-code-card__stdout-label{font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em;flex-shrink:0}
.wb-code-card__stdout-wrap{display:flex;flex-direction:column;gap:3px;flex-shrink:0;min-height:0}
.wb-code-card,.wb-code-card *{scrollbar-width:thin;scrollbar-color:#475569 #0f172a}
.wb-code-card ::-webkit-scrollbar{width:8px;height:8px}
.wb-code-card ::-webkit-scrollbar-track{background:#0f172a;border-radius:4px}
.wb-code-card ::-webkit-scrollbar-thumb{background:#475569;border-radius:4px;border:2px solid #0f172a}
.wb-code-card ::-webkit-scrollbar-thumb:hover{background:#64748b}
.wb-code-card ::-webkit-scrollbar-corner{background:#0f172a}
.wb-code-card ::-webkit-scrollbar-button{display:none}
.wb-code-card__stdout::-webkit-resizer{background:linear-gradient(135deg,transparent 40%,#475569 40%,#475569 55%,transparent 55%);border-radius:0 0 8px 0}
`;

/**
 * Виджет «Код». Всё, что делает один участник, сразу видят остальные:
 * набор текста (по паузе ~150 мс), смену языка, запуск и вывод консоли
 * по ходу выполнения, обновление превью HTML.
 * Python → поле source, JavaScript → поле js.
 */
export function renderCodeObject(obj: SceneObject, opts: CodeRenderOpts = {}): HTMLElement {
  const g = document.createElement("div");
  g.classList.add("wb-scene-obj", "wb-scene-obj--code");
  g.dataset.objectId = obj.id;

  const w = Math.max(280, Number(obj.w) || 520);
  const h = Math.max(240, Number(obj.h) || 420);
  placeHtmlObject(g, obj, w, h);

  const state = readCodeState(obj as Parameters<typeof readCodeState>[0]);
  const canEdit = Boolean(opts.canEdit);

  const card = el("div", "wb-code-card");
  card.style.width = "100%";
  card.style.height = "100%";
  card.style.boxSizing = "border-box";
  card.dataset.language = state.language;
  card.dataset.runSeq = String(state.runSeq);
  card.setAttribute("data-object-id", obj.id);
  stopProp(card);

  const style = el("style");
  style.textContent = FO_CSS;
  card.appendChild(style);

  const head = el("div", "wb-code-card__head");
  const title = el("span", "wb-code-card__title");
  title.textContent = "Код";
  head.appendChild(title);

  if (opts.onRequestSelect) {
    const editBtn = el("button", "wb-code-card__edit");
    editBtn.type = "button";
    editBtn.textContent = "Передвинуть";
    editBtn.title = "Выделить для перемещения, размера, комментария";
    editBtn.setAttribute("data-object-id", obj.id);
    const selectSelf = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      opts.onRequestSelect?.(obj.id);
    };
    editBtn.addEventListener("pointerdown", selectSelf);
    editBtn.addEventListener("click", selectSelf);
    head.appendChild(editBtn);
  }
  card.appendChild(head);

  const tabs = el("div", "wb-code-card__tabs");
  for (const lang of LANGS) {
    const tab = el("button", "wb-code-card__tab" + (lang === state.language ? " is-active" : ""));
    tab.type = "button";
    tab.textContent = languageLabel(lang);
    tab.disabled = !canEdit;
    tab.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (!canEdit) return;
      // Смена вкладки — структурный UI; нельзя оставлять «typing»-preserve старого DOM.
      clearCodeWidgetTyping(obj.id);
      opts.onCodeAction?.(obj.id, { type: "setLanguage", language: lang });
    });
    tabs.appendChild(tab);
  }
  card.appendChild(tabs);

  const body = el(
    "div",
    "wb-code-card__body" + (state.language === "web" ? " wb-code-card__body--web" : "")
  );
  const senders: LiveTextSender[] = [];

  function makeEditor(
    label: string,
    field: "source" | "js" | "html" | "css",
    language: CodeEditorLanguage,
    value: string,
    onCommit: (v: string) => void,
    minHeight = "140px"
  ): HTMLDivElement {
    const wrap = el("div", "wb-code-card__editor");
    const lab = el("label", "wb-code-card__label");
    lab.textContent = label;
    const host = el("div", "wb-code-card__editor-host");
    host.dataset.codeEditor = "1";
    host.dataset.codeField = field;
    host.addEventListener("pointerdown", (ev) => ev.stopPropagation());

    const sender = createLiveTextSender(host, {
      initial: value,
      enabled: canEdit,
      read: () => getCodeEditor(host)?.getValue() ?? value,
      send: onCommit,
    });
    senders.push(sender);

    mountCodeEditor(host, {
      language,
      value,
      readOnly: !canEdit,
      minHeight,
      onInput: () => {
        if (!canEdit) return;
        codeTypingIds.add(obj.id);
        sender.schedule();
      },
      onFocus: () => {
        codeTypingIds.add(obj.id);
      },
      onBlur: () => {
        sender.flush();
        window.setTimeout(() => {
          if (!host.contains(document.activeElement)) codeTypingIds.delete(obj.id);
        }, 0);
      },
    });

    wrap.appendChild(lab);
    wrap.appendChild(host);
    return wrap;
  }

  let previewFrame: HTMLIFrameElement | null = null;

  if (state.language === "web") {
    const editors = el("div", "wb-code-card__editors");
    editors.appendChild(
      makeEditor("HTML", "html", "html", state.html, (html) =>
        opts.onCodeAction?.(obj.id, { type: "setHtml", html })
      , "100px")
    );
    editors.appendChild(
      makeEditor("CSS", "css", "css", state.css, (css) =>
        opts.onCodeAction?.(obj.id, { type: "setCss", css })
      , "100px")
    );
    editors.appendChild(
      makeEditor("JS", "js", "javascript", state.js, (js) =>
        opts.onCodeAction?.(obj.id, { type: "setJs", js })
      , "80px")
    );
    body.appendChild(editors);

    const previewWrap = el("div", "wb-code-card__preview");
    const previewLab = el("div", "wb-code-card__label");
    previewLab.textContent = "Превью";
    previewFrame = el("iframe", "wb-code-card__iframe");
    previewFrame.title = "HTML preview";
    previewFrame.sandbox.add("allow-scripts");
    previewFrame.srcdoc = buildWebSrcDoc(state.html, state.css, state.js);
    previewWrap.appendChild(previewLab);
    previewWrap.appendChild(previewFrame);
    body.appendChild(previewWrap);
  } else if (state.language === "javascript") {
    body.appendChild(
      makeEditor(
        "JavaScript",
        "js",
        "javascript",
        state.js,
        (js) => opts.onCodeAction?.(obj.id, { type: "setJs", js }),
        "200px"
      )
    );
  } else {
    body.appendChild(
      makeEditor(
        "Python",
        "source",
        "python",
        state.source,
        (source) => opts.onCodeAction?.(obj.id, { type: "setSource", source }),
        "200px"
      )
    );
  }
  card.appendChild(body);

  const foot = el("div", "wb-code-card__foot");
  const runBtn = el("button", "wb-code-card__run");
  runBtn.type = "button";
  runBtn.textContent = state.language === "web" ? "Обновить превью" : "▶ Запуск";

  const status = el("div", "wb-code-card__status");
  const statusDot = el("span", "wb-code-card__status-dot");
  statusDot.setAttribute("aria-hidden", "true");
  const statusText = el("span", "wb-code-card__status-text");
  status.appendChild(statusDot);
  status.appendChild(statusText);

  const outWrap = el("div", "wb-code-card__stdout-wrap");
  const outLab = el("div", "wb-code-card__stdout-label");
  outLab.textContent = "Консоль (тяните за угол ↓)";
  const out = el("pre", "wb-code-card__stdout");
  out.textContent = state.stdout || STDOUT_PLACEHOLDER;
  out.title = "Потяните за правый нижний угол, чтобы изменить высоту";
  outWrap.appendChild(outLab);
  outWrap.appendChild(out);

  runBtn.addEventListener("click", async (ev) => {
    ev.stopPropagation();
    const setRunning = (on: boolean, label?: string) => {
      card.classList.toggle("is-running", on);
      runBtn.disabled = on;
      runBtn.classList.toggle("is-busy", on);
      status.classList.toggle("is-visible", on);
      if (label) statusText.textContent = label;
    };

    setRunning(true, "Подготовка…");
    runBtn.textContent = state.language === "web" ? "Обновление…" : "Выполняется…";
    out.classList.add("is-live");
    out.textContent = "▶ Запуск…\n";

    // Сначала — недосланный текст, чтобы у других запускался тот же код.
    for (const sender of senders) sender.flush();

    const live = { ...state };
    const edited = readCodeValuesFromCard(card, state.language);
    if (edited.source != null) live.source = edited.source;
    if (edited.js != null) live.js = edited.js;
    if (edited.html != null) live.html = edited.html;
    if (edited.css != null) live.css = edited.css;
    if (state.language === "web" && previewFrame) {
      previewFrame.srcdoc = buildWebSrcDoc(live.html, live.css, live.js);
    }

    // Другие видят запуск: превью HTML обновляется у всех, вывод консоли идёт по ходу.
    let streamTimer = 0;
    let streamed = "";
    const streamStdout = (text: string, now = false) => {
      if (!canEdit || !opts.onCodeAction) return;
      streamed = text;
      if (now) {
        if (streamTimer) window.clearTimeout(streamTimer);
        streamTimer = 0;
        opts.onCodeAction(obj.id, { type: "setStdout", stdout: streamed });
        return;
      }
      if (streamTimer) return;
      streamTimer = window.setTimeout(() => {
        streamTimer = 0;
        opts.onCodeAction?.(obj.id, { type: "setStdout", stdout: streamed });
      }, STDOUT_STREAM_MS);
    };
    if (canEdit) {
      // Своё превью уже обновлено — чтобы оно не перезагрузилось второй раз.
      card.dataset.runSeq = String(Number(card.dataset.runSeq || 0) + 1);
      opts.onCodeAction?.(obj.id, { type: "run" });
    }
    if (state.language !== "web") streamStdout("▶ Запуск…", true);

    try {
      const result = await runCodeWidget(live, {
        onPhase: (phase, detail) => {
          if (phase === "done" || phase === "error") return;
          setRunning(true, runPhaseLabel(phase, detail));
        },
        onOutput: (stdout) => {
          out.textContent = stdout || "▶ Выполнение…";
          streamStdout(stdout || "▶ Выполнение…");
        },
      });
      const text = result.ok
        ? result.stdout
        : [result.stdout, result.error ? "Ошибка: " + result.error : ""]
            .filter(Boolean)
            .join("\n");
      out.textContent = text || "(пусто)";
      streamStdout(text, true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      out.textContent = "Ошибка: " + msg;
      streamStdout("Ошибка: " + msg, true);
    } finally {
      out.classList.remove("is-live");
      setRunning(false);
      runBtn.textContent = state.language === "web" ? "Обновить превью" : "▶ Запуск";
    }
  });

  foot.appendChild(runBtn);
  foot.appendChild(status);

  const downloadBtn = el("button", "wb-code-card__download");
  downloadBtn.type = "button";
  downloadBtn.textContent = "⬇ Скачать";
  downloadBtn.title =
    state.language === "web"
      ? "Скачать HTML-файл с CSS и JS"
      : state.language === "javascript"
        ? "Скачать .js"
        : "Скачать .py";
  downloadBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    const edited = readCodeValuesFromCard(card, state.language);
    let filename: string;
    let text: string;
    if (state.language === "web") {
      const html = edited.html ?? state.html;
      const css = edited.css ?? state.css;
      const js = edited.js ?? state.js;
      filename = "code.html";
      text = buildWebSrcDoc(html, css, js);
    } else if (state.language === "javascript") {
      filename = "code.js";
      text = edited.js ?? state.js;
    } else {
      filename = "code.py";
      text = edited.source ?? state.source;
    }
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  });
  foot.appendChild(downloadBtn);

  if (state.language === "python") {
    const hint = el("span", "wb-code-card__hint");
    const sec = Math.round(CODE_RUN_TIMEOUT_MS / 1000);
    hint.textContent = `import: ${pythonImportHint()} · лимит ${sec} с · бесконечный цикл остановится`;
    foot.appendChild(hint);
  } else if (state.language === "javascript") {
    const hint = el("span", "wb-code-card__hint");
    const sec = Math.round(CODE_RUN_TIMEOUT_MS / 1000);
    hint.textContent = `import: ${jsImportHint()} · лимит ${sec} с`;
    foot.appendChild(hint);
  }
  card.appendChild(foot);
  card.appendChild(outWrap);

  g.appendChild(card);
  return g;
}
